import React, { useMemo, useState } from 'react';
import { Card, Input, Select, Space, Tag, Typography } from 'antd';
import StandardTable, { createActionColumn } from '../../../../components/common/StandardTable';
import { useNavigate } from 'react-router-dom';
import { useGetQaqcPipeWelder, useGetCompanies, useGetPersonnel } from '../../../../hooks/genericHooks';
import type { QaqcPipeWelder, Company, VwPersonnel } from '../../../../types';
import Button from '../../../../components/common/Button';

const { Title } = Typography;

export default function QaqcPipeWelderList() {
  const navigate = useNavigate();
  const { data: welderResp, isLoading } = useGetQaqcPipeWelder();
  const { data: companiesResp } = useGetCompanies();
  const { data: personnelResp } = useGetPersonnel();

  const [search, setSearch] = useState<string>('');
  const [companyId, setCompanyId] = useState<number | undefined>();
  const [occupation, setOccupation] = useState<string | undefined>();
  const [status, setStatus] = useState<'active' | 'inactive' | 'terminated' | undefined>();

  const companies = (companiesResp?.data as unknown as Company[]) || [];
  const personnel = (personnelResp?.data as unknown as VwPersonnel[]) || [];

  const data: QaqcPipeWelder[] = (welderResp?.data as unknown as QaqcPipeWelder[]) || [];

  const occupationOptions = useMemo(() => {
    const set = new Set<string>();
    personnel.forEach(p => { if (p.occupation) set.add(p.occupation); });
    return Array.from(set).sort((a,b)=>a.localeCompare(b)).map(o => ({ value: o, label: o }));
  }, [personnel]);

  const companyOptions = useMemo(() => {
    const ids = new Set<number>((data || []).map(r => r.company_id));
    const opts = Array.from(ids).map(id => {
      const c = companies.find(cc => cc.id === id);
      return { value: id, label: c ? c.name : String(id) };
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [data, companies]);

  const filtered = useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return data.filter(r => {
      if (companyId && r.company_id !== companyId) return false;
      if (status && r.employment_status !== status) return false;
      if (occupation) {
        const pers = personnel.find(p => p.pers_id === r.personnel_id);
        if ((pers?.occupation || '').toLowerCase() !== occupation.toLowerCase()) return false;
      }
      if (tokens.length) {
        const pers = personnel.find(p => p.pers_id === r.personnel_id);
        const full = `${pers?.first_name || ''} ${pers?.last_name || ''}`.trim();
        const hay = `${r.welder_name} ${full}`.toLowerCase();
        if (!tokens.every(t => hay.includes(t))) return false;
      }
      return true;
    });
  }, [data, search, companyId, status, occupation, personnel]);

  const columns: any[] = [
    { title: 'Welder', dataIndex: 'welder_name', key: 'welder_name', width: 40},
    { title: 'Personnel', key: 'personnel', width: 140, render: (_: any, rec: QaqcPipeWelder) => {
        const p = personnel.find(pp => pp.pers_id === rec.personnel_id);
        return p ? `${p.first_name} ${p.last_name}` : rec.personnel_id;
      }
    },
    { title: 'Company', key: 'company', width: 140, render: (_: any, rec: QaqcPipeWelder) => {
        const c = companies.find(cc => cc.id === rec.company_id);
        return c ? c.name : rec.company_id;
      }
    },
    { title: 'WPS', dataIndex: 'wps_list', key: 'wps_list', width: 300, render: (arr?: string[]) => (arr || []).map(w => <Tag key={w}>{w}</Tag>) },
    { title: 'Status', dataIndex: 'employment_status', key: 'employment_status', width: 50, render: (s: QaqcPipeWelder['employment_status']) => <Tag color={s==='active'?'green': s==='inactive'?'gold':'red'}>{s}</Tag> },
    createActionColumn<QaqcPipeWelder>({
      title: 'Actions',
      width: 140,
      onView: (rec) => navigate(`/qaqc/piping/tools/welders/view/${rec.id}`, { state: { record: rec } }),
      onEdit: (rec) => navigate(`/qaqc/piping/tools/welders/edit/${rec.id}`, { state: { record: rec } }),
      showView: true,
      showDelete: false,
    })
  ];

  return (
    <Card title={<Title level={4} style={{ margin: 0 }}>Welders</Title>} size="small">
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search welder or personnel"
            style={{ width: 260 }}
            onSearch={setSearch}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
          <Select
            allowClear
            showSearch
            style={{ width: 220 }}
            placeholder="Filter company"
            options={companyOptions}
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            value={companyId}
            onChange={(v) => setCompanyId(v as number | undefined)}
            onClear={() => setCompanyId(undefined)}
          />
          <Select
            allowClear
            showSearch
            style={{ width: 200 }}
            placeholder="Filter occupation"
            options={occupationOptions}
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            value={occupation}
            onChange={(v) => setOccupation(v ? String(v) : undefined)}
            onClear={() => setOccupation(undefined)}
          />
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="Filter status"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'terminated', label: 'Terminated' },
            ]}
            value={status}
            onChange={(v) => setStatus(v as any)}
            onClear={() => setStatus(undefined)}
          />
          <Button action="add" type="primary" onClick={() => navigate('/qaqc/piping/tools/welders/create')}>New Welder</Button>
        </Space>
        <StandardTable
          rowKey={(r: QaqcPipeWelder) => String(r.id)}
          columns={columns}
          dataSource={filtered}
          loading={isLoading}
          pagination={{ pageSize: 20 }}
          enableExport
        />
      </Space>
    </Card>
  );
}
